/**
 * Mass balance of a machine run (F6.6) — the phone's provisional copy of the server check.
 *
 *   residual   = kilos in − (flush + product + by-product + waste)
 *   residual % = residual ÷ kilos in × 100
 *   yield %    = product ÷ (kilos in − flush) × 100
 *   pass       = |residual %| ≤ step loss limit  AND  min ≤ yield % ≤ max  AND  residual ≠ 0
 *
 * All weights are integer grams net of the sack; percentages are integer BASIS POINTS
 * (1 bp = 0.01 %), rounded half up once, so the phone never compares floats. The server's
 * answer (after sync) always replaces this.
 */
import type { Grams } from '@/lib/weight'

export type BalanceKind = 'RESIDUAL' | 'YIELD' | 'SURPLUS' | 'EXACT_ZERO'

export interface BalanceFigures {
  input_g: Grams
  flush_g: Grams
  product_g: Grams
  byproduct_g: Grams
  reject_g: Grams
}

export interface BalanceLimits {
  /** The step's loss limit in bp (0.5 % → 50). */
  loss_limit_bp: number
  /** The machine's usual yield range in bp (68 % → 6800); null when the step has no band. */
  min_bp: number | null
  max_bp: number | null
}

export interface BalanceResult extends BalanceFigures {
  output_g: Grams
  residual_g: Grams
  /** Signed; null when nothing went in. */
  residual_bp: number | null
  yield_bp: number | null
  kinds: BalanceKind[]
  pass: boolean
}

/** a ÷ b × 10 000, rounded half away from zero, as an integer (b > 0). */
export function ratioBp(a: number, b: number): number {
  if (b <= 0) throw new RangeError('denominator must be positive')
  const num = BigInt(Math.round(a)) * 10_000n
  const den = BigInt(Math.round(b))
  const neg = num < 0n
  const abs = neg ? -num : num
  const q = (abs * 2n + den) / (2n * den)
  return Number(neg ? -q : q)
}

/** Percent (from the server's settings) → basis points. */
export function pctToBp(pct: number | null | undefined): number | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null
  return Math.round(pct * 100)
}

/** "0.15 %" style text from basis points (2 decimals, or 1 when `short`). */
export function formatBp(bp: number, short = false): string {
  const sign = bp < 0 ? '−' : ''
  const abs = Math.abs(bp)
  if (short) {
    const tenths = Math.round(abs / 10)
    return `${sign}${Math.trunc(tenths / 10)}.${tenths % 10}%`
  }
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}%`
}

export function computeBalance(f: BalanceFigures, limits: BalanceLimits): BalanceResult {
  const output_g = f.flush_g + f.product_g + f.byproduct_g + f.reject_g
  const residual_g = f.input_g - output_g
  const residual_bp = f.input_g > 0 ? ratioBp(residual_g, f.input_g) : null
  const base = f.input_g - f.flush_g
  const yield_bp = base > 0 ? ratioBp(f.product_g, base) : null

  const kinds: BalanceKind[] = []
  if (residual_bp !== null) {
    if (residual_g === 0) kinds.push('EXACT_ZERO')
    else if (Math.abs(residual_bp) > limits.loss_limit_bp)
      kinds.push(residual_g < 0 ? 'SURPLUS' : 'RESIDUAL')
  }
  if (
    yield_bp !== null &&
    limits.min_bp !== null &&
    limits.max_bp !== null &&
    (yield_bp < limits.min_bp || yield_bp > limits.max_bp)
  )
    kinds.push('YIELD')

  return {
    ...f,
    output_g,
    residual_g,
    residual_bp,
    yield_bp,
    kinds,
    pass: f.input_g > 0 && kinds.length === 0,
  }
}

/** Pack line (F7.4): packed kilos = pouches × mean spot weight; unaccounted against the limit. */
export interface PackBalance {
  input_g: Grams
  packed_g: Grams
  leftover_g: Grams
  spillage_g: Grams
  unaccounted_g: Grams
  unaccounted_bp: number | null
  in_limit: boolean
}

export function computePackBalance(args: {
  input_g: Grams
  pouches_good: number
  spot_weights_g: readonly number[]
  /** Used when no spot weight was typed (declared pouch size). */
  net_g: Grams
  leftover_g: Grams
  spillage_g: Grams
  loss_limit_bp: number
}): PackBalance {
  const n = args.spot_weights_g.length
  const sum = args.spot_weights_g.reduce((a, b) => a + b, 0)
  // pouches × mean = pouches × sum ÷ n, rounded half up once.
  const packed_g =
    n > 0
      ? Number((BigInt(args.pouches_good) * BigInt(sum) * 2n + BigInt(n)) / (2n * BigInt(n)))
      : args.pouches_good * args.net_g
  const out = packed_g + args.leftover_g + args.spillage_g
  const unaccounted_g = args.input_g - out
  const unaccounted_bp = args.input_g > 0 ? ratioBp(unaccounted_g, args.input_g) : null
  return {
    input_g: args.input_g,
    packed_g,
    leftover_g: args.leftover_g,
    spillage_g: args.spillage_g,
    unaccounted_g,
    unaccounted_bp,
    in_limit: unaccounted_bp !== null && Math.abs(unaccounted_bp) <= args.loss_limit_bp,
  }
}
